// The saloon's on-screen layer, in the visor's projected light (the HUD's G.U.L.P. VISOR MK.II
// look, client/hud/hud.ts): phosphor type with a faint colour split and glow, hairlines and
// 45-degree chamfers, no backing cards. Captions for whoever is talking, the reply choices when
// you're sat at the bar with Pop, the button hints, the posse's ready banner, the fade.
//
// Controller first: d-pad / stick to move, ✕ to pick, ○ to back out; keyboard and mouse work too.
// PS5 glyphs on a pad, key caps on a keyboard. Plain DOM over the canvas, only touched when
// something changes.

import { firstPad, psGlyph } from '../input/padnav';

/** the visor's light (hud.ts PHOSPHOR) and its caution amber, as CSS */
export const VISOR_CSS = `
.vz { --ph: 228,255,244; --amb: 255,176,58; font-family: 'Space Grotesk', system-ui, sans-serif; color: rgba(var(--ph), .9);
  text-shadow: -0.6px 0 rgba(255,90,80,.28), 0.6px 0 rgba(90,220,255,.28), 0 0 10px rgba(var(--ph), .32), 0 1px 2px rgba(0,0,0,.55); }
.vz .psg { width: 1.25em; height: 1.25em; vertical-align: -0.28em; margin-right: 7px; }
.vz .psg-ring { fill: none; stroke: rgba(var(--ph), .75); stroke-width: 1.2; }
.vz .psg-mark { fill: none; stroke: rgba(var(--ph), .95); stroke-width: 1.6; }
.vz .cap-k { display: inline-block; min-width: 1.3em; padding: 0 6px; margin-right: 7px; font-size: .8em; font-weight: 600; letter-spacing: 1px;
  border: 1px solid rgba(var(--ph), .5); clip-path: polygon(5px 0, 100% 0, 100% calc(100% - 5px), calc(100% - 5px) 100%, 0 100%, 0 5px); text-align: center; }
`;

const CSS = VISOR_CSS + `
.sal-layer { position: fixed; inset: 0; pointer-events: none; z-index: 8; }
.sal-cap { position: absolute; left: 50%; bottom: 17vh; transform: translateX(-50%); width: min(860px, calc(100vw - 32px)); text-align: center;
  font-size: clamp(18px, 2.05vw, 27px); font-weight: 500; line-height: 1.34; letter-spacing: .2px; opacity: 0; transition: opacity .35s; }
.sal-cap.on { opacity: 1; }
/* at the gun rack the readout has the left column: Pop talks from the top right, clear of it */
.sal-cap.high { bottom: auto; top: max(3.5vh, 14px); left: auto; right: max(3vw, 16px); transform: none; width: min(560px, calc(100vw - 2 * max(3vw, 16px) - 500px)); text-align: right; font-size: clamp(16px, 1.6vw, 21px); }
@media (max-width: 720px) { .sal-cap.high { left: 16px; right: 16px; width: auto; text-align: center; } }
.sal-cap::before, .sal-choices::before { content: ''; position: absolute; inset: -18px -40px; z-index: -1; pointer-events: none;
  background: radial-gradient(closest-side, rgba(4,12,12,.5), rgba(4,12,12,.28) 60%, transparent); }
.sal-cap .who { display: inline-flex; align-items: center; gap: 8px; font-size: 12px; font-weight: 600; text-transform: uppercase; letter-spacing: 3.5px;
  color: rgba(var(--amb), .95); margin-bottom: 6px; }
.sal-cap .who::before, .sal-cap .who::after { content: ''; width: 26px; height: 1px; background: rgba(var(--amb), .6); }
.sal-cap.you .who { color: rgba(var(--ph), .7); }
.sal-cap.you .who::before, .sal-cap.you .who::after { background: rgba(var(--ph), .35); }
.sal-cap.you .txt { color: rgba(var(--ph), .78); font-style: italic; }
.sal-choices { position: absolute; left: 50%; bottom: calc(17vh - 64px); transform: translateX(-50%); display: flex; gap: 6px; flex-wrap: wrap; justify-content: center;
  width: min(980px, calc(100vw - 24px)); opacity: 0; transition: opacity .25s; pointer-events: none; }
.sal-choices.on { opacity: 1; pointer-events: auto; }
.sal-ch { position: relative; padding: 7px 16px 6px; font-size: clamp(14px, 1.3vw, 17px); font-weight: 500; letter-spacing: .4px; color: rgba(var(--ph), .55);
  cursor: pointer; white-space: nowrap; transition: color .12s; }
.sal-ch::before, .sal-ch::after { content: ''; position: absolute; width: 9px; height: 9px; opacity: 0; transition: opacity .12s; }
.sal-ch::before { left: 0; top: 0; border-left: 1.5px solid rgba(var(--amb), .9); border-top: 1.5px solid rgba(var(--amb), .9); }
.sal-ch::after { right: 0; bottom: 0; border-right: 1.5px solid rgba(var(--amb), .9); border-bottom: 1.5px solid rgba(var(--amb), .9); }
.sal-ch.sel { color: rgba(var(--ph), 1); }
.sal-ch.sel::before, .sal-ch.sel::after { opacity: 1; }
.sal-ch .psg { display: none; }
.sal-ch.sel .psg { display: inline-block; }
.sal-hints { position: absolute; left: 50%; bottom: 5.5vh; transform: translateX(-50%); display: flex; gap: 26px; font-size: 14px; font-weight: 500;
  letter-spacing: 2px; text-transform: uppercase; color: rgba(var(--ph), .75); opacity: 0; transition: opacity .2s; white-space: nowrap; }
.sal-hints.on { opacity: 1; }
.sal-prompt { position: absolute; left: 50%; top: 61%; transform: translate(-50%, 0); padding: 7px 18px 6px; font-size: 16px; font-weight: 600; letter-spacing: 3px;
  text-transform: uppercase; opacity: 0; transition: opacity .2s; white-space: nowrap; }
.sal-prompt::before { content: ''; position: absolute; inset: 0; border: 1px solid rgba(var(--ph), .32); background: rgba(var(--ph), .04);
  clip-path: polygon(8px 0, 100% 0, 100% calc(100% - 8px), calc(100% - 8px) 100%, 0 100%, 0 8px); }
.sal-prompt.on { opacity: 1; }
.sal-ready { position: absolute; left: 50%; top: 22px; transform: translateX(-50%); display: flex; gap: 14px; align-items: center; padding: 6px 18px;
  font-size: 13px; font-weight: 500; letter-spacing: 2.5px; text-transform: uppercase; opacity: 0; transition: opacity .3s; white-space: nowrap; }
.sal-ready::before { content: ''; position: absolute; inset: 0; border-top: 1px solid rgba(var(--ph), .3); border-bottom: 1px solid rgba(var(--ph), .3); }
.sal-ready.on { opacity: 1; }
.sal-ready b { color: rgba(var(--amb), .95); font-weight: 600; }
.sal-ready .dots { display: flex; gap: 6px; }
.sal-ready .dot { width: 10px; height: 10px; transform: rotate(45deg); border: 1.2px solid rgba(var(--ph), .6); }
.sal-ready .dot.y { background: rgba(var(--amb), .9); border-color: rgba(var(--amb), 1); box-shadow: 0 0 8px rgba(var(--amb), .7); }
.sal-fade { position: absolute; inset: 0; background: #000; opacity: 0; transition: opacity .25s; }
.sal-title { position: absolute; left: 50%; top: 40%; transform: translate(-50%, -50%); text-align: center; opacity: 0; transition: opacity .6s; }
.sal-title.on { opacity: 1; }
.sal-title .t1 { font-size: clamp(30px, 5vw, 58px); font-weight: 600; }
.sal-title .t2 { font-size: 15px; letter-spacing: 5px; text-transform: uppercase; margin-top: 6px; opacity: .8; }
`;

export interface Row { label: string; act: () => void; dim?: boolean }
export type Glyph = 'cross' | 'circle' | 'square' | 'triangle';
/** a button hint: the pad glyph, its keyboard cap, what it does */
export interface Hint { g: Glyph; key: string; text: string }

/** Edge-triggered pad reading for the saloon's menus (they run while the game's input is held). */
export class PadEdges {
  private prev = 0;
  private held = 0;
  private next = 0;
  /** 1 ✕, 2 ○, 4 up, 8 down, 16 left, 32 right, 64 □, 128 △ (d-pad or a held stick, with repeat) */
  poll(): number {
    const gp = firstPad();
    if (!gp) { this.prev = 0; return 0; }
    const on = (i: number) => !!gp.buttons[i]?.pressed;
    let b = 0;
    if (on(0)) b |= 1; if (on(1)) b |= 2; if (on(12)) b |= 4; if (on(13)) b |= 8; if (on(14)) b |= 16; if (on(15)) b |= 32; if (on(2)) b |= 64; if (on(3)) b |= 128;
    const pressed = b & ~this.prev;
    this.prev = b;
    // the left stick as a d-pad. The step goes to whichever axis leads, once on the push and then on a
    // repeat while it's held the same way. A different direction only counts after the stick has come back
    // near the middle, so a diagonal push or the spring-back on release never takes a second step.
    const ax = gp.axes[0] ?? 0, ay = gp.axes[1] ?? 0;
    const lead = Math.abs(ay) >= Math.abs(ax) ? (ay < 0 ? 4 : 8) : (ax < 0 ? 16 : 32);
    const mag = Math.hypot(ax, ay);
    const now = performance.now() / 1000;
    let stick = 0;
    if (this.held) {
      if (mag < 0.35) this.held = 0;
      else if (lead === this.held && mag > 0.6 && now > this.next) { this.next = now + 0.16; stick = lead; }
    } else if (mag > 0.6) { this.held = lead; this.next = now + 0.42; stick = lead; }
    return pressed | stick;
  }
  /** what's held right now doesn't count as a press (a menu opening on the button that opened it) */
  swallow() { this.poll(); this.prev = 0xff; }
}

export class SaloonUI {
  readonly root = document.createElement('div');
  private cap = document.createElement('div');
  private choicesEl = document.createElement('div');
  private hints = document.createElement('div');
  private prompt = document.createElement('div');
  private ready = document.createElement('div');
  private fadeEl = document.createElement('div');
  private titleEl = document.createElement('div');
  private rows: Row[] = [];
  private sel = 0;
  private open = false;
  private onBack: (() => void) | null = null;
  /** typewriter */
  private capText = ''; private capShown = 0; private capUntil = 0; private capWho = ''; private capYou = false;
  private promptKey = '';
  private hintKey = '';
  private readyKey = '';
  private pad = new PadEdges();
  glyphs: 'kbm' | 'pad' = 'kbm';
  /** called per typed character (Pop's voice ticks) */
  onType: () => void = () => {};
  onMove: () => void = () => {};
  onPick: () => void = () => {};
  private keyHandler = (e: KeyboardEvent) => this.onKey(e);

  constructor() {
    if (!document.getElementById('sal-css')) {
      const st = document.createElement('style');
      st.id = 'sal-css'; st.textContent = CSS;
      document.head.appendChild(st);
    }
    this.root.className = 'sal-layer vz';
    this.fadeEl.className = 'sal-fade';
    this.cap.className = 'sal-cap';
    this.choicesEl.className = 'sal-choices';
    this.hints.className = 'sal-hints';
    this.prompt.className = 'sal-prompt';
    this.ready.className = 'sal-ready';
    this.titleEl.className = 'sal-title';
    this.root.append(this.cap, this.choicesEl, this.hints, this.prompt, this.ready, this.titleEl, this.fadeEl);
    document.body.appendChild(this.root);
    window.addEventListener('keydown', this.keyHandler, true);
  }

  dispose() {
    window.removeEventListener('keydown', this.keyHandler, true);
    this.root.remove();
  }

  /** the reply choices are up */
  get choosing() { return this.open; }
  /** the current caption is still typing out */
  get typing() { return this.capUntil > 0 && this.capShown < this.capText.length; }

  glyph(g: Glyph, key: string) { return this.glyphs === 'pad' ? psGlyph(g) : `<span class="cap-k">${esc(key)}</span>`; }

  // ---------------------------------------------------------------- captions
  /** a spoken line, typed out. `you` styles it as your own cowboy's reply. */
  caption(text: string, who = 'Pop', hold = 3.2, you = false) {
    this.capText = text; this.capShown = 0; this.capWho = who; this.capYou = you;
    this.capUntil = performance.now() / 1000 + text.length * 0.032 + hold;
    this.cap.classList.add('on');
    this.cap.classList.toggle('you', you);
    this.paintCap();
  }
  /** captions up top (while the rack's readout has the bottom of the screen) */
  captionHigh(on: boolean) { this.cap.classList.toggle('high', on); }
  clearCaption() { this.capUntil = 0; this.cap.classList.remove('on'); }

  private paintCap() {
    const who = this.capWho ? `<span class="who">${esc(this.capWho)}</span><br>` : '';
    this.cap.innerHTML = who + `<span class="txt">${esc(this.capText.slice(0, Math.floor(this.capShown)))}</span>`;
  }

  // ---------------------------------------------------------------- prompt, hints, ready banner
  /** the "□ Sit" style prompt over the world, or null */
  setPrompt(glyph: Glyph | null, text: string, key = 'E') {
    const k = glyph ? glyph + text + this.glyphs : '';
    if (k === this.promptKey) return;
    this.promptKey = k;
    if (!glyph) { this.prompt.classList.remove('on'); return; }
    this.prompt.innerHTML = this.glyph(glyph, key) + esc(text);
    this.prompt.classList.add('on');
  }

  /** the strip of button hints along the bottom */
  setHints(list: Hint[] | null) {
    const k = list ? list.map((h) => h.g + h.text).join('|') + this.glyphs : '';
    if (k === this.hintKey) return;
    this.hintKey = k;
    if (!list || !list.length) { this.hints.classList.remove('on'); return; }
    this.hints.innerHTML = list.map((h) => `<span>${this.glyph(h.g, h.key)}${esc(h.text)}</span>`).join('');
    this.hints.classList.add('on');
  }

  setReady(show: boolean, n: number, need: number, mine: boolean, atBar = false) {
    const key = show ? `${n}/${need}/${mine}/${atBar}` : '';
    if (key === this.readyKey) return;
    this.readyKey = key;
    if (!show) { this.ready.classList.remove('on'); return; }
    let dots = '';
    for (let i = 0; i < need; i++) dots += `<span class="dot${i < n ? ' y' : ''}"></span>`;
    const what = mine ? '' : atBar ? 'Tell Pop when you\'re ready' : n === 0 ? 'Take a stool at the bar to saddle up' : '';
    const say = what || (mine ? 'Saddled up · waiting on the posse' : 'Posse saddling up');
    this.ready.innerHTML = `<span class="dots">${dots}</span><span><b>${n}/${need}</b> ${say}</span>`;
    this.ready.classList.add('on');
  }

  title(t1: string, t2: string, on: boolean) {
    this.titleEl.innerHTML = `<div class="t1">${esc(t1)}</div><div class="t2">${esc(t2)}</div>`;
    this.titleEl.classList.toggle('on', on);
  }

  /** 0..1 black */
  fade(v: number) { this.fadeEl.style.opacity = String(v); }

  // ---------------------------------------------------------------- reply choices
  /** Put up what you can say back. ○ / Esc calls onBack. */
  choices(rows: Row[], onBack: () => void) {
    if (!this.open) this.sel = 0;
    this.rows = rows; this.onBack = onBack;
    this.sel = Math.min(this.sel, rows.length - 1);
    this.open = true;
    this.choicesEl.classList.add('on');
    this.pad.swallow();
    this.paintRows();
  }
  closeChoices() {
    this.open = false; this.rows = []; this.onBack = null;
    this.choicesEl.classList.remove('on');
  }

  private paintRows() {
    this.choicesEl.innerHTML = '';
    this.rows.forEach((r, i) => {
      const d = document.createElement('div');
      d.className = 'sal-ch' + (i === this.sel ? ' sel' : '');
      d.innerHTML = (this.glyphs === 'pad' ? psGlyph('cross') : '') + esc(r.label);
      d.onmouseenter = () => { if (this.sel !== i) { this.sel = i; this.paintRows(); this.onMove(); } };
      d.onclick = () => { this.sel = i; this.pick(); };
      this.choicesEl.appendChild(d);
    });
  }

  private pick() {
    const r = this.rows[this.sel];
    if (!r) return;
    this.onPick();
    r.act();
  }

  private move(d: number) {
    if (!this.rows.length) return;
    this.sel = (this.sel + d + this.rows.length) % this.rows.length;
    this.paintRows();
    this.onMove();
  }

  /** the caption's typewriter jumps to the end (✕ while Pop is mid-line) */
  skipTyping() { if (this.typing) { this.capShown = this.capText.length; this.paintCap(); } }

  private onKey(e: KeyboardEvent) {
    if (!this.open) return;
    const k = e.code;
    let used = true;
    if (k === 'ArrowLeft' || k === 'KeyA' || k === 'ArrowUp' || k === 'KeyW') this.move(-1);
    else if (k === 'ArrowRight' || k === 'KeyD' || k === 'ArrowDown' || k === 'KeyS') this.move(1);
    else if (k === 'Enter' || k === 'Space' || k === 'KeyE') this.pick();
    else if (k === 'Escape' || k === 'Backspace') this.onBack?.();
    else if (/^Digit[1-9]$/.test(k)) { const i = Number(k.slice(5)) - 1; if (i < this.rows.length) { this.sel = i; this.paintRows(); this.pick(); } }
    else used = false;
    if (used) { e.preventDefault(); e.stopImmediatePropagation(); }
  }

  // ---------------------------------------------------------------- per frame
  update(dt: number) {
    const now = performance.now() / 1000;
    if (this.capUntil > 0) {
      if (this.capShown < this.capText.length) {
        const before = Math.floor(this.capShown);
        this.capShown = Math.min(this.capText.length, this.capShown + dt * 38);
        if (Math.floor(this.capShown) !== before) { this.paintCap(); if (before % 3 === 0 && !this.capYou) this.onType(); }
      } else if (now > this.capUntil) this.clearCaption();
    }
    if (!this.open) return;
    const p = this.pad.poll();
    if (p & 1) this.pick();
    else if (p & 2) this.onBack?.();
    else if (p & (4 | 16)) this.move(-1);
    else if (p & (8 | 32)) this.move(1);
  }
}

export function esc(s: string) {
  return s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]!));
}
